/**
 * What a failed request says about the session (backlog 39b90d28). Only the
 * server saying "no session" means signed out. A rate limit, a server error or
 * no connection says nothing about it: reading those as signed out sent people
 * to Sign in with a perfectly good session, most often after a few refreshes.
 */
export type SessionTrouble = 'busy' | 'unreachable';

export const SESSION_ENDED_ELSEWHERE = 'Your session was ended from another device.';
export const SESSION_ENDED_HERE =
  'Your session on this browser has ended. It may have timed out, or another tab may have switched accounts.';

/** Shown on the waiting screen while the check keeps retrying. */
export const SESSION_TROUBLE_LABEL: Record<SessionTrouble, string> = {
  busy: 'Too many requests in the last minute. Trying again, no need to refresh',
  unreachable: 'Can’t reach tinycms right now. Trying again',
};

type RequestError = {
  graphQLErrors?: ReadonlyArray<{ extensions?: Record<string, unknown> }>;
  networkError?: { statusCode?: number } | null;
};

const graphQLErrorsOf = (err: unknown) => (err as RequestError | null)?.graphQLErrors || [];
const statusOf = (err: unknown) => (err as RequestError | null)?.networkError?.statusCode;

export function isNoSession(err: unknown): boolean {
  return (
    statusOf(err) === 401 ||
    graphQLErrorsOf(err).some((e) => e.extensions?.code === 'UNAUTHENTICATED')
  );
}

/** The session was ended on purpose (all devices, a password change), not just absent. */
export function wasSessionEnded(err: unknown): boolean {
  return graphQLErrorsOf(err).some((e) => e.extensions?.sessionEnded === true);
}

export function sessionTrouble(err: unknown): SessionTrouble {
  return statusOf(err) === 429 ? 'busy' : 'unreachable';
}

/** What a sign-in form says when the request never got an answer; null if it did. */
export function signInTrouble(err: unknown): string | null {
  if (statusOf(err) === 429)
    return 'Too many tries from this connection. Wait a minute and try again.';
  if ((err as RequestError | null)?.networkError) {
    return 'Can’t reach tinycms right now. Check your connection and try again.';
  }
  return null;
}
